import {
  DsBoxModule,
  DsBreadcrumbModule,
  DsButtonModule,
  DsHeaderModule,
  DsHeaderTagConfiguration,
  DsHeaderUserInfo,
  DsLoadingAreaModule,
  DsLoadingAreaService,
  DsMenuModule,
  DsNavigationBarModule,
  DsNavigationItem,
  DsNavigationMetaItem,
} from '@bmw-ds/components';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  selectCurrentUserRoles,
  selectIsImpersonating,
  selectLoading,
} from './state/current-user/current-user.selectors';
import { FedevAuthService, IFedevAuthUserProfile } from '@bmw-fedev/auth';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { ToastExtendedService } from './common/toast-extended.service';
import { ErrorToastComponent } from './common/error-toast/error-toast.component';
import { injectActiveLanguageSignal } from './shared/utils/active-language';
import { filter, firstValueFrom, map, startWith } from 'rxjs';
import { Roles } from './shared/enums/roles.enum';
import { CurrentUserService } from './shared/services/current-user.service';
import { dispatchViewableQueueSummaries } from './shared/working-queue/working-queue-permissions';
import { selectQueueSummaries } from './state/working-queues/working-queues.selectors';
import { HeaderComponent } from './header/header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { stopImpersonation } from './state/current-user/current-user.actions';
import { environment } from '../environments/environment';

@Component({
  selector: 'tqc-root',
  standalone: true,
  imports: [
    DsNavigationBarModule,
    DsHeaderModule,
    DsBoxModule,
    DsButtonModule,
    DsLoadingAreaModule,
    RouterOutlet,
    ErrorToastComponent,
    DsBreadcrumbModule,
    HeaderComponent,
    DsMenuModule,
    TranslatePipe,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly store = inject(Store);
  private readonly loadingAreaService = inject(DsLoadingAreaService);
  private readonly authService = inject(FedevAuthService);
  private readonly toastExtendedService = inject(ToastExtendedService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly destroyRef = inject(DestroyRef);

  private readonly toneByStage: Record<
    string,
    DsHeaderTagConfiguration['tone']
  > = {
    PROD: 'positive',
    INT: 'caution',
    DEV: 'info',
  };
  readonly isImpersonating = toSignal(
    this.store.select(selectIsImpersonating),
    {
      initialValue: false,
    }
  );
  stopImpersonation(): void {
    this.store.dispatch(stopImpersonation());
  }

  public loading$ = this.store.select(selectLoading);

  private readonly activeLanguage = injectActiveLanguageSignal();

  public readonly currentUserLoaded = toSignal(
    this.loading$.pipe(map((l) => !l)),
    {
      initialValue: false,
    }
  );
  public readonly environmentTagConfig = signal<
    Partial<DsHeaderTagConfiguration>
  >({
    label: environment.stage,
    tone: this.toneByStage[environment.stage] ?? 'info',
  });
  public readonly userInfo: WritableSignal<Partial<DsHeaderUserInfo>> = signal(
    {}
  );
  private readonly userRoles = toSignal(
    this.store.select(selectCurrentUserRoles),
    {
      initialValue: [],
    }
  );

  public readonly hasClaimHandlerSettlementRole = toSignal(
    this.store
      .select(selectCurrentUserRoles)
      .pipe(
        map((roles) =>
          roles.some(
            (role) =>
              role.name === Roles.CLAIM_HANDLER_SETTLEMENT && role.isActive
          )
        )
      ),
    { initialValue: false }
  );
  public readonly hasClaimHandlerRecoveryRole = toSignal(
    this.store
      .select(selectCurrentUserRoles)
      .pipe(
        map((roles) =>
          roles.some(
            (role) =>
              role.name === Roles.CLAIM_HANDLER_RECOVERY && role.isActive
          )
        )
      ),
    { initialValue: false }
  );
  public readonly hasSuperUserRole = toSignal(
    this.store
      .select(selectCurrentUserRoles)
      .pipe(
        map((roles) =>
          roles.some((role) => role.name === Roles.SUPER_USER && role.isActive)
        )
      ),
    { initialValue: false }
  );
  public readonly hasDamageEntryAccess = computed(() =>
    this.userRoles().some(
      (r) =>
        r.isActive &&
        (r.name === Roles.SUPER_USER || r.name === Roles.DAMAGE_ENTRY)
    )
  );

  private readonly liabilityQueues = this.store.selectSignal(
    selectQueueSummaries('liability')
  );
  private readonly claimQueues = this.store.selectSignal(
    selectQueueSummaries('claim')
  );
  private readonly recoveryQueues = this.store.selectSignal(
    selectQueueSummaries('recovery')
  );

  private readonly _workingQueuesInitEffect = effect(() => {
    if (this.currentUserLoaded()) {
      dispatchViewableQueueSummaries(this.store, this.userRoles());
    }
  });

  public readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  public readonly currentQueueParam = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((params) => params.get('queue'))
    ),
    { initialValue: this.activatedRoute.snapshot.queryParamMap.get('queue') }
  );

  public readonly navigationItems = computed(() => {
    this.activeLanguage();
    const url = this.currentUrl();
    const currentQueue = this.currentQueueParam();
    const isLiabilityWorkingQueuesRoute =
      url?.includes('/liabilities/working-queues') ?? false;
    const isClaimWorkingQueuesRoute =
      url?.includes('/claims/working-queues') ?? false;
    const isRecoveryWorkingQueuesRoute =
      url?.includes('/recoveries/working-queues') ?? false;
    const isDashboardRoute = url === '/dashboard';
    const isDamagesRoute = url?.startsWith('/damages') ?? false;
    const isVehicleMovementsRoute =
      url?.startsWith('/vehicleMovements') ?? false;
    const isReportingRoute = url?.startsWith('/reporting') ?? false;
    const availableLiabilityQueues = this.liabilityQueues().filter(
      (wq) => wq.totalCount != null && wq.totalCount !== '0'
    );

    const availableClaimQueues = this.claimQueues().filter(
      (wq) => wq.totalCount != null && wq.totalCount !== '0'
    );

    const availableRecoveryQueues = this.recoveryQueues().filter(
      (wq) => wq.totalCount != null && wq.totalCount !== '0'
    );

    const liabilityNavItems: DsNavigationItem[] = availableLiabilityQueues.map(
      (queue) => {
        const icon = queue.status === 'TODO' ? 'list_edit' : 'information';

        return {
          label: this.translate.instant(`liabilities.status.${queue.name}`),
          routerLink: ['/liabilities/working-queues'],
          navigationExtras: { queryParams: { queue: queue.name } },
          icon: icon,
          active: isLiabilityWorkingQueuesRoute && currentQueue === queue.name,
        };
      }
    );

    const claimNavItems: DsNavigationItem[] =
      availableClaimQueues.map((queue) => {
        const icon = queue.status === 'TODO' ? 'list_edit' : 'information';

        return {
          label: this.translate.instant(`claims.status.${queue.name}`),
          routerLink: ['/claims/working-queues'],
          navigationExtras: { queryParams: { queue: queue.name } },
          icon: icon,
          active: isClaimWorkingQueuesRoute && currentQueue === queue.name,
        };
      }) ?? [];
    if (this.hasClaimHandlerSettlementRole() || this.hasSuperUserRole()) {
      claimNavItems.push({
        label: this.translate.instant('navigation.settlements.bordereau'),
        routerLink: ['/settlement/bordereau'],
      });
    }

    const recoveryNavItems: DsNavigationItem[] =
      availableRecoveryQueues.map((queue) => {
        const icon = queue.status === 'TODO' ? 'list_edit' : 'information';

        return {
          label: this.translate.instant(`dashboard.recoveries.${queue.name}`),
          routerLink: ['/recoveries/working-queues'],
          navigationExtras: { queryParams: { queue: queue.name } },
          icon: icon,
          active: isRecoveryWorkingQueuesRoute && currentQueue === queue.name,
        };
      }) ?? [];

    if (this.hasClaimHandlerRecoveryRole() || this.hasSuperUserRole()) {
      recoveryNavItems.push({
        label: this.translate.instant('navigation.recoveries.bordereau'),
        routerLink: ['/recovery/bordereau'],
      });
    }
    const navigationItems = [
      {
        label: this.translate.instant('navigation.home'),
        routerLink: ['/dashboard'],
        icon: 'home',
        active: isDashboardRoute,
      },
      ...(this.hasDamageEntryAccess()
        ? [
            {
              label: this.translate.instant('navigation.damageEntry'),
              routerLink: ['/damages'],
              icon: 'edit',
              active: isDamagesRoute,
            },
          ]
        : []),
      {
        label: this.translate.instant('navigation.vehicleMovements'),
        routerLink: ['/vehicleMovements'],
        icon: 'car',
        active: isVehicleMovementsRoute,
      },
      {
        label: this.translate.instant('navigation.reporting'),
        routerLink: ['/reporting'],
        icon: 'file_chart',
        active: isReportingRoute,
      },
    ];

    if (availableLiabilityQueues.length > 0) {
      navigationItems.push({
        label: this.translate.instant('navigation.liabilities.liability'),
        icon: 'folder_checked',
        active: isLiabilityWorkingQueuesRoute,
        children: liabilityNavItems,
      } as unknown as (typeof navigationItems)[0]);
    }

    if (claimNavItems.length > 0) {
      navigationItems.push({
        label: this.translate.instant('navigation.settlements.claim'),
        icon: 'hand_coin',
        active: isClaimWorkingQueuesRoute,
        children: claimNavItems,
      } as unknown as (typeof navigationItems)[0]);
    }
    if (availableRecoveryQueues.length > 0) {
      navigationItems.push({
        label: this.translate.instant('navigation.recoveries.recovery'),
        icon: 'arrow_go_back',
        active: isRecoveryWorkingQueuesRoute,
        children: recoveryNavItems,
      } as unknown as (typeof navigationItems)[0]);
    }
    return navigationItems as DsNavigationItem[];
  });

  public readonly metaItems: WritableSignal<DsNavigationMetaItem[]> = signal(
    []
  );
  private readonly errorToastComponent =
    viewChild.required(ErrorToastComponent);

  constructor() {
    const authService = this.authService;

    effect(() => {
      this.setUserInfo(authService.userProfile() ?? undefined);
    });

    effect(() => {
      this.userRoles();
      this.activeLanguage();
      void this.setMetaItems();
    });
  }

  public setUserInfo(user: IFedevAuthUserProfile | undefined) {
    if (!user) {
      this.userInfo.set({
        username: this.authService.currentUser(),
        department: undefined,
        strongAuth: false,
      });
      return;
    }

    this.userInfo.set({
      username: this.authService.currentUser(),
      department: user['departmentnumber'],
      strongAuth: user['acr'] !== '0',
    });
  }

  ngOnInit() {
    this.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        if (loading) {
          this.loadingAreaService.addLoadingArea({
            backdropVariant: 'solid',
            id: 'loading-user-profile',
            loadingText: 'Please wait while data is loaded',
            loadingTitle: 'Loading user profile',
          });
        } else if (!loading) {
          this.loadingAreaService.removeLoadingArea('loading-user-profile');
        }
      });
  }

  ngAfterViewInit() {
    this.toastExtendedService.setDefaultErrorTemplate(
      this.errorToastComponent().errorToastTemplate()
    );
  }

  public logout() {
    return this.authService.revokeTokenAndLogout();
  }

  private async setMetaItems(): Promise<void> {
    let items: DsNavigationMetaItem[] = [];

    if (
      await firstValueFrom(
        this.currentUserService.hasRole([
          Roles.SUPER_USER,
          Roles.ADMIN_COUNTRY,
          Roles.ADMIN_LOGISTIC_PARTNER,
        ])
      )
    ) {
      items = [
        ...items,
        {
          label: this.translate.instant('navigation.administration.admin'),
          routerLink: ['/admin'],
          icon: 'settings',
        },
      ];
    }

    items = [
      ...items,
      {
        label: this.translate.instant('navigation.help'),
        routerLink: ['/'],
        icon: 'help',
      },
    ];

    this.metaItems.set(items);
  }
}
